import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AdsContext, getPlayerBrand } from '../ads';
import { useExternalScript } from '../common/hooks';
import { settings } from '../../settings';
import { PreferenceContext } from '../context';

let agnoplayPatched = false;

export const agnoplayPatch = () => {
  if (!agnoplayPatched) {
    agnoplayPatched = true;
    document.addEventListener('agnoLoaded', (e) => {
      const playerId = e.detail.playerId;
      const player = window.AGNO.Players.getPlayerById(playerId).agnoInstance;
      if (!player) {
        return;
      }
      player.addEventListener('playing', () => {
        window.AGNO.pauseAllExcept(playerId);
      });
    });
  }
};

const useAgnoplayScript = () => {
  const script1 = useExternalScript(settings.PLAYER_SCRIPT_1);
  const script2 = useExternalScript(settings.PLAYER_SCRIPT_2);
  return ![script1, script2].includes('loading');
};

export const VideoPlayer = ({ item, mediaIndex, related = [], embedded, videoEndedAction }) => {
  const { date, media, nsfw, nopreroll, id, tags, title, description, still } = item;
  const blockAds = Boolean(nsfw || nopreroll || embedded);

  // !component parameters update is not handled,
  // if new item or mediaIndex is set while player already created for previous item it will not work
  // whole component should be removed and added again to play new video

  const { search, hash } = useLocation();
  const ref = useRef({ current: null });
  const playerLoaded = useAgnoplayScript();
  const [activeItemId, setActiveItemId] = useState(null);
  const [activeTimeStamp, setActiveTimeStamp] = useState(null);
  const [activePlayerId, setActivePlayerId] = useState(null);
  const [loadNewPlayer, setLoadNewPlayer] = useState(false);
  const {
    preference: { upNextActive },
  } = useContext(PreferenceContext);
  const { kaching, kachingInitialized } = useContext(AdsContext);

  const { duration, variants } = media[mediaIndex];
  const dateFormat = date ? date.slice(0, 19).split('T') : ['-', '-'];
  const mediaId = variants[0].uri.match(/[0-9a-z-]{36}/)
    ? variants[0].uri.match(/[0-9a-z-]{36}/)[0]
    : '';
  const widthForVariant = (version) => {
    switch (version) {
      case '720p':
        return 720;
      case '1080p':
        return 1080;
      case 'tablet':
        return 480;
      case 'mobile':
        return 270;
      default:
        return 480;
    }
  };

  useEffect(() => {
    const hasId = hash && hash.includes('id=');
    const hasTime = hash && hash.includes('time=');
    const urlId = hash && hasId ? hash.split('id=')[1] : undefined;
    const urlTime = hash && hasId && hasTime ? hash.split('time=')[1].split('id=')[0] : undefined;

    if (urlId && urlTime && (urlId !== activeItemId || urlTime !== activeTimeStamp)) {
      setLoadNewPlayer(true);
      setActiveItemId(urlId);
      setActiveTimeStamp(urlId);
      if (ref.current && ref.current.id === urlId) {
        const timeOut = playerLoaded ? 300 : 500;
        setTimeout(() => {
          ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, timeOut);
      }
    }
  }, [hash]);

  useEffect(() => {
    if (id !== activeItemId) {
      setLoadNewPlayer(true);
      setActiveItemId(id);
    }
  }, [id, activeItemId]);

  useEffect(() => {
    if (
      playerLoaded &&
      (!activePlayerId || loadNewPlayer) &&
      ((kaching && kachingInitialized) || blockAds || embedded)
    ) {
      let anchorIndex = 0;
      if (hash.includes('time')) {
        const m = hash.match(/sid=\d+_[a-z\d]{8}-(\d)/);
        if (m && m[1]) {
          anchorIndex = Number(m[1]);
        }
      }
      const config = {
        kaching: blockAds ? null : kaching,
        noads: !!embedded,
        upnext: videoEndedAction ? true : upNextActive,
        countdown_duration: videoEndedAction ? 2 : 10,
        autoplay: !embedded && mediaIndex === anchorIndex,
        brand: getPlayerBrand(),
        videoId: mediaId,
        url: window.location.href,
        socialUrl: window.location.href,
        recommendations: true,
        personalized_recommendations: true,
        premium_text: 'PREMIUM',
        keyboard_controls: true,
        related,
        video_metadata: {
          duration,
          title,
          description,
          date_created: dateFormat[0] + ' ' + dateFormat[1],
          format: 'Dumpert',
          tags: tags ? tags.split(' ').join(',') : '',
          category: 'Dumpert',
          publisher: 'Dumpert',
        },
      };

      if (nsfw || nopreroll) {
        config.ads = [
          {
            integration: 'google-ima',
            sources: nsfw ? settings.ADS_NSFW_URL : settings.ADS_NSFA_URL,
          },
        ];
      }
      const thumbrail = variants.find((x) => x.version === 'thumbrail');
      if (thumbrail) {
        const folderUrl = thumbrail.uri.split('/').slice(0, -1).join('/');
        config.has_thumbnails = true;
        config.thumbnailsPreview = {
          spriteImageUrl: `${folderUrl}/thumbs.png`,
          spriteMetadataJsonUrl: `${folderUrl}/storyboard.json`,
        };
      }
      if (!blockAds) {
        config.has_video_object = true;
        config.video_metadata.upload_date = dateFormat[0] + ' ' + dateFormat[1];
      }
      const stream = variants.find((x) => x.version === 'stream');
      const liveStream = variants.find((x) => x.version === 'agnoplay');
      if (liveStream) {
        // special item of live streaming from dupmert studio
        config.brand = 'dumpert-live';
        config.videoId = 'tmg2';
      } else if (stream) {
        config.stream_source = 'custom';
        config.custom_source = {
          source: stream.uri,
          thumbnail: still,
          title,
        };
      } else {
        config.stream_source = 'custom';
        config.custom_source = {
          title,
          thumbnail: still,
          source: variants.map((v) => ({
            width: widthForVariant(v.version),
            src: v.uri,
          })),
        };
      }
      if (search) {
        // to preview unpublished or secret post on staging
        // dumpert admin ads token to item link and check for header when video is loaded by agnoplay.
        // it is not needed on production because media.dumpert.nl doesn't protect video files with authorization
        try {
          const token = new URLSearchParams(search).get('access_token');
          if (token) {
            config.vhsSourceRequestHeaders = {
              Authorization: `Bearer ${token}`,
            };
          }
        } catch (e) {
          /* no op */
        }
      }
      const playerId = window.AGNO.insertPlayer(config, ref.current);
      if (playerId) {
        setLoadNewPlayer(false);
        setActivePlayerId(playerId);
      } else {
        throw new Error('Player not inserted, check window.AGNO.insertPlayer');
      }
    }
  });

  useEffect(
    () => () => {
      if (activePlayerId) {
        try {
          window.AGNO.disposePlayer(activePlayerId);
        } catch (e) {
          /* no-op */
        }

        // remove tags added by AGNO player
        [].filter
          .call(
            document.getElementsByTagName('script'),
            (x) => x.type === 'application/ld+json' && x.id && x.id.startsWith('videoObject-'),
          )
          .forEach((x) => x.remove());
      }
    },
    [activePlayerId],
  );

  useEffect(agnoplayPatch, []);

  return (
    <>
      <div ref={ref} id={mediaIndex > 0 ? `${id}-${mediaIndex}` : id}></div>
    </>
  );
};
